import React, { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { EntitiesTableList } from '../../../components';
import { fetchHealthOrganizations, disableHO, clearHOInfo } from '../../../store/health_organizations';
import { resendInvitationForUser, sendNewPassword, loginAsDifferentRole } from '../../../store/authorization';
import { renderColumns, transformData } from './renderColumns';
import { URLS } from '../../../constants/constants';
import { redirectToPage } from '../../../utils/helpers';
import { usePagination } from '../../../hooks/usePagination';
import { useSearch } from '../../../hooks/useSearch';
import { Navigation } from '../../../utils/navigation';

const Health_Organizations = ({
  disableHO,
  fetchHealthOrganizations,
  limit,
  currentPage,
  total,
  totalPages,
  healthOrganizations, 
  sendNewPassword,
  resendInvitationForUser,
  clearHOInfo,
  isEnterprise,
  loginAsDifferentRole,
  isViewDashboardMode
}) => {
  const hasFetchedOrganizationsRef = useRef(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [selectedOrganizationStatus, setSelectedOrganizationStatus] = useState('ACTIVE');
  const navigate = Navigation();
  usePagination(navigate);
  const { searchValue, highlighted, handleChangeSearch } = useSearch(
    fetchHealthOrganizations,
    () => ({ organizationStatus: selectedOrganizationStatus, isEnterprise }),
  );

  useEffect(() => {
    if (hasFetchedOrganizationsRef.current) return;
    hasFetchedOrganizationsRef.current = true;
    fetchHealthOrganizations({ Navigation: navigate, isEnterprise });
    return;
  }, [fetchHealthOrganizations, navigate, isEnterprise]);

  useEffect(() => () => {clearHOInfo(); return;}, [clearHOInfo]);

  const handleManageOrganization = useCallback(
    (id) => () =>  navigate.push(`/health_organizations/${id}/edit`),
    [navigate],
  );

  const switchOrganizationStatus = useCallback((status) => () => disableHO({...status,Navigation:navigate}), [disableHO,navigate]);

  const handleSendPassword = useCallback((id) => () => sendNewPassword(id), [sendNewPassword]);

  const handleResendInvitation = useCallback((id) => () => resendInvitationForUser(id), [resendInvitationForUser]);

  const handleOrganizationStatus = (value) => {
    setSelectedOrganizationStatus(value);
    redirectToPage(URLS.HEALTH_ORGANIZATIONS, 1, navigate);
    fetchHealthOrganizations({ value: searchValue, organizationStatus: value, filtered: true, Navigation: navigate, isEnterprise });
  };

  const handleChangePage = (page) => {
    redirectToPage(URLS.HEALTH_ORGANIZATIONS, page, navigate);
    fetchHealthOrganizations({ value: searchValue, Navigation: navigate, isEnterprise, organizationStatus: selectedOrganizationStatus });
  };

  const handleViewDashboard = useCallback(
    (viewDashboardParams) => () => loginAsDifferentRole({...viewDashboardParams,Navigation:navigate}),
    [loginAsDifferentRole],
  );

  const handleToggleExpandRow = useCallback((rowKey) => {
    setExpandedRowKeys((prev) =>
      prev.includes(rowKey) ? prev.filter((key) => key !== rowKey) : [...prev, rowKey],
    );
  }, []);

  // Handle row clicks - modified to handle tree data
  const handleRowClick = useCallback(
    (record) => (event) => {
      const isActionClick = event?.target?.closest?.('button, a, input, label');
      if (isActionClick) return;

      if (record.children && record.children.length > 0) {
        handleToggleExpandRow(record.key);
      }
    },
    [handleToggleExpandRow]
  );

  const handleAddClinic = (parentOrgId) => () => {
    navigate.push(`/health_organizations/new?parentOrgId=${parentOrgId}`);
  };

  const tableData = useMemo(() => {
    return transformData({
      data: healthOrganizations,
      handleManageOrganization,
      switchOrganizationStatus,
      handleSendPassword,
      handleResendInvitation,
      handleViewDashboard,
      isEnterpriseAdmin: isEnterprise,
      handleAddClinic,
      isViewDashboardMode
    });
  }, [
    healthOrganizations,
    handleManageOrganization,
    switchOrganizationStatus,
    handleSendPassword,
    handleViewDashboard,
    handleResendInvitation,
  ]);

  const columns = useMemo(() => {
    return healthOrganizations?.length > 0
      ? renderColumns(searchValue, highlighted, {
          expandedRowKeys,
          onToggleExpand: handleToggleExpandRow,
        })
      : null;
  }, [expandedRowKeys, handleToggleExpandRow, healthOrganizations, highlighted, searchValue]);
  
  // Check if data has tree structure
  const hasTreeData = useMemo(() => {
    return tableData?.some(item => item.children && item.children.length > 0);
  }, [tableData]);

  useEffect(() => {
    const validKeys = new Set();
    const collectTreeKeys = (nodes = []) => {
      nodes.forEach((node) => {
        validKeys.add(node.key);
        if (node.children?.length) collectTreeKeys(node.children);
      });
    };
    collectTreeKeys(tableData);
    setExpandedRowKeys((prev) => prev.filter((key) => validKeys.has(key)));
  }, [tableData]);

  // Configure expandable props for the tree table
  const expandableConfig = useMemo(() => {
    if (!hasTreeData) return null;
    
    return {
      defaultExpandAllRows: false,
      expandRowByClick: false,
      showExpandColumn: false,
      indentSize: 20,
      expandedRowKeys,
      onExpand: (expanded, record) => {
        setExpandedRowKeys((prev) =>
          expanded ? [...new Set([...prev, record.key])] : prev.filter((key) => key !== record.key),
        );
      },
    };
  }, [expandedRowKeys, hasTreeData]);

  return (
    <EntitiesTableList
      title='Organizations'
      addNewBtnTitle='Add New Organization'
      Navigation={navigate}
      columns={columns}
      data={tableData}
      handleChangePage={handleChangePage}
      handleRowClick={handleRowClick}
      showAddNewButton
      handleChangeSearch={handleChangeSearch}
      searchValue={searchValue}
      limit={limit}
      currentPage={currentPage}
      total={total}
      totalPages={totalPages}
      showOrganizationStatus
      selectedOrganizationStatus={selectedOrganizationStatus}
      handleOrganizationStatus={handleOrganizationStatus}
      expandable={expandableConfig}
    />
  );
};

const mapStateToProps = (state) => ({
  healthOrganizations: state.healthOrganizations.healthOrganizations,
  isEnterprise: state.authorization.isEnterprise,
  limit: state.healthOrganizations.limit,
  currentPage: state.healthOrganizations.currentPage || 1,
  total: state.healthOrganizations.total,
  totalPages: state.healthOrganizations.totalPages,
  isViewDashboardMode: state?.authorization?.viewDashboardMode?.active,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchHealthOrganizations,
      disableHO,
      sendNewPassword,
      clearHOInfo,
      resendInvitationForUser,
      loginAsDifferentRole,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(Health_Organizations);
